import { useProductFilters } from './useProductFilters';
import { usePreviousProps } from '../../hooks/usePreviousProps';
import { useRef } from 'react';
import isEqual from 'lodash/isEqual';
import union from 'lodash/union';
import { ProductFilter } from '../../../models/Product';
import { Aggregations } from '../../../models/Aggregations';
import { useQuery } from '@tanstack/react-query';
import { GlobalReactQueryOptions } from '../../config';
import { FetchAggregations } from '../../api/listContent/fetchAggregations';
import { mapAggregations } from '../../api/listContent/mapAggregations';
import { filterAggregations } from '../../api/listContent/filterAggregations';
import { markEmptyAggregations } from '../../api/listContent/markEmptyAggregations';
import { markSeeAllAggregations } from '../../api/listContent/markSeeAllAggregations';

export type UseAggregationsQuery = {
    readonly status: 'error' | 'loading' | 'success' | 'idle';
    readonly error: any;
    readonly data: Aggregations;
    readonly isFetching: boolean;
};
export type QueryKeyBuilder = (options: { filters: ProductFilter; expandedFilterGroups: string[] }) => any[];
export type UseAggregationsQueryParams = {
    visibleFilterGroups: string[];
    buildQueryKey: QueryKeyBuilder;
    fetchAggregations: FetchAggregations;
};

const emptyAggregations: Aggregations = {};

export const useAggregationsQuery = ({
    visibleFilterGroups,
    buildQueryKey,
    fetchAggregations,
    expandedFilterGroups,
    seeAllFilterEntries
}: UseAggregationsQueryParams & {
    expandedFilterGroups: string[];
    seeAllFilterEntries: string[];
}): UseAggregationsQuery => {
    const { filters } = useProductFilters();
    const prevFilter = usePreviousProps(filters);

    const expandedRef = useRef<string[]>([]);
    if (!isEqual(prevFilter, filters)) {
        expandedRef.current = expandedFilterGroups;
    }

    const expandedFilterGroupsSuperset = union(expandedFilterGroups, expandedRef.current);
    const filterMap: ProductFilter = Object.fromEntries(
        Object.entries({
            ...mapCategoriesToProductFilter(expandedFilterGroupsSuperset),
            ...filters
        }).map(([key, value]) => {
            if (seeAllFilterEntries.includes(key)) {
                return [key, { ...value, includeAll: true }];
            } else {
                return [key, value];
            }
        })
    );

    const lastData = useRef<Aggregations>({});

    const { status, data, error, isFetching } = useQuery<Aggregations>(
        buildQueryKey({ filters: filterMap, expandedFilterGroups: expandedFilterGroupsSuperset }),
        ({ signal }) =>
            fetchAggregations({ filters: filterMap, signal })
                .then(data => mapAggregations(data))
                .then(data => filterAggregations(data, visibleFilterGroups))
                .then(data => markEmptyAggregations(data, filterMap))
                .then(data => markSeeAllAggregations(data, filterMap)),
        {
            ...GlobalReactQueryOptions,
            initialData: lastData.current,
            onSuccess: (aggregations: Aggregations) => {
                lastData.current = aggregations;
                expandedRef.current = expandedFilterGroupsSuperset;
            }
        }
    );

    return {
        isFetching,
        status,
        error,
        data: data ?? emptyAggregations
    };
};

export const mapCategoriesToProductFilter = (categories: string[]): ProductFilter =>
    categories.reduce(
        (previousValue, currentValue) => ({
            ...previousValue,
            [currentValue]: {
                includeFilterValues: [],
                excludeFilterValues: []
            }
        }),
        {}
    );
